import { Document, Page as PdfPage, Text, View, StyleSheet } from '@react-pdf/renderer';
import { registerPdfFonts } from '../cap/pdf-fonts';

registerPdfFonts();

// ─── El expediente, impreso ──────────────────────────────────────────────────
//
// PARA QUÉ. El comité de TMAI se reúne, y no todos llegan con pantalla. Este
// documento existe para esa mesa: se reparte, se lee y se anota.
//
// LA REGLA QUE LO GOBIERNA: es EL MISMO expediente, no un resumen aparte. La
// portada la calcula `buildCapDossierCover` en el dominio —la misma llamada que
// hace la pantalla— y la evidencia se imprime del mismo paquete. Si alguien
// decide sobre el papel y otro sobre la pantalla, tienen que estar mirando lo
// mismo; un resumen redactado por separado se desincroniza en la primera
// corrección y nadie se entera hasta que dos personas discuten con cifras
// distintas.
//
// LLEVA SELLO. Ronda, versión, la auditoría que lo respalda y la fecha de
// emisión van en la cabecera. Un PDF circulando por correo sin procedencia no
// es una ayuda: es un documento que alguien puede citar sin saber si quedó
// viejo.
//
// i18n-AGNÓSTICO, como los demás documentos: recibe los textos ya resueltos.
// Acá el lector es el REVISOR, así que se resuelven en SU idioma — a diferencia
// del paquete de la propuesta, que va en inglés porque su destinatario es TMAI
// como institución.

export interface CapDossierPdfFinding {
  severity: 'blocking' | 'concern' | 'note';
  severityLabel: string;
  text: string;
}

export interface CapDossierPdfArea {
  name: string;
  minTcu: number;
  actualTcu: number;
  met: boolean;
}

export interface CapDossierPdfCourse {
  code: string;
  name: string;
  area: string;
  tcu: string;
}

export interface CapDossierPdfSection {
  heading: string;
  body?: string;
}

export interface CapDossierPdfData {
  brand: { networkName: string; primaryColor: string };
  /** Sello de procedencia: sin esto el documento no se puede situar. */
  stamp: {
    programName: string;
    seminaryName: string;
    capCode: string;
    level: string;
    round: number;
    version: number;
    submittedOn: string;
    auditRunOn?: string;
    generatedOn: string;
  };
  cover: {
    statusLabel: string;
    stacked?: string;
    tcuLine: string;
    evidenceLine: string;
    findings: CapDossierPdfFinding[];
    nothingToFlag?: string;
    reading: string;
  };
  areas: CapDossierPdfArea[];
  courses: CapDossierPdfCourse[];
  sections: CapDossierPdfSection[];
  attestations: string[];
  /** Las observaciones ya enviadas, cuando la ronda está decidida. */
  observations?: string;
  labels: {
    title: string;
    coverTitle: string;
    auditTitle: string;
    coursesTitle: string;
    sectionsTitle: string;
    attestationsTitle: string;
    observationsTitle: string;
    area: string;
    minTcu: string;
    actualTcu: string;
    state: string;
    met: string;
    short: string;
    round: string;
    version: string;
    submittedOn: string;
    auditRunOn: string;
    generatedOn: string;
    footerDisclaimer: string;
  };
}

const SEVERITY_COLOR: Record<CapDossierPdfFinding['severity'], string> = {
  blocking: '#b91c1c',
  concern: '#b45309',
  note: '#1d4ed8',
};

const styles = StyleSheet.create({
  page: {
    fontFamily: 'EB Garamond',
    fontSize: 10,
    color: '#1f2937',
    paddingTop: 42,
    paddingBottom: 56,
    paddingHorizontal: 48,
  },
  header: { marginBottom: 14 },
  networkName: { fontSize: 15, fontWeight: 700 },
  docTitle: { fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: '#6b7280' },
  brandBar: { width: 64, height: 3, borderRadius: 2, marginVertical: 7 },
  programName: { fontSize: 14, fontWeight: 700, marginTop: 6 },
  seminaryName: { fontSize: 10, color: '#4b5563' },
  stampRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginTop: 6 },
  stampItem: { marginRight: 12 },
  stampLabel: { fontSize: 7, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 },
  stampValue: { fontSize: 9, fontWeight: 700 },

  cover: {
    borderWidth: 1.5,
    borderColor: '#111827',
    borderRadius: 4,
    padding: 10,
    marginBottom: 16,
  },
  coverHead: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 2 },
  coverTitle: { fontSize: 12, fontWeight: 700 },
  statusChip: {
    fontSize: 8,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    paddingVertical: 2,
    paddingHorizontal: 5,
    borderRadius: 3,
    backgroundColor: '#f3f4f6',
  },
  coverMeta: { fontSize: 9, color: '#4b5563', marginBottom: 8 },
  finding: { flexDirection: 'row', gap: 6, marginBottom: 5 },
  findingTag: {
    fontSize: 7,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    width: 58,
    paddingTop: 1,
  },
  findingText: { flex: 1, fontSize: 9.5, lineHeight: 1.35 },
  reading: { fontSize: 8.5, color: '#6b7280', marginTop: 8, fontStyle: 'italic' },

  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    marginTop: 10,
    marginBottom: 5,
    borderBottomWidth: 0.5,
    borderBottomColor: '#d1d5db',
    paddingBottom: 2,
  },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 0.4,
    borderBottomColor: '#e5e7eb',
    paddingVertical: 3,
  },
  th: { fontSize: 7.5, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 },
  cellName: { flex: 3 },
  cellNum: { flex: 1, textAlign: 'right' },
  cellState: { flex: 1.2, textAlign: 'right' },
  shortText: { color: '#b91c1c', fontWeight: 700 },

  sectionBlock: { marginBottom: 9 },
  sectionHeading: { fontSize: 10, fontWeight: 700, marginBottom: 2 },
  sectionBody: { fontSize: 9.5, lineHeight: 1.4, color: '#374151' },
  bullet: { fontSize: 9.5, marginBottom: 2 },
  observations: {
    borderWidth: 1,
    borderColor: '#d97706',
    borderRadius: 4,
    backgroundColor: '#fffbeb',
    padding: 8,
    fontSize: 9.5,
    lineHeight: 1.4,
    color: '#92400e',
  },

  footer: {
    position: 'absolute',
    bottom: 30,
    left: 48,
    right: 48,
    fontSize: 7.5,
    color: '#9ca3af',
    textAlign: 'center',
  },
  pageNumber: {
    position: 'absolute',
    bottom: 18,
    left: 0,
    right: 0,
    fontSize: 8,
    color: '#9ca3af',
    textAlign: 'center',
  },
});

function Stamp({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <View style={styles.stampItem}>
      <Text style={styles.stampLabel}>{label}</Text>
      <Text style={styles.stampValue}>{value}</Text>
    </View>
  );
}

export function CapDossierPdfDocument({ data }: { data: CapDossierPdfData }): JSX.Element {
  const { brand, stamp, cover, areas, courses, sections, attestations, labels } = data;

  return (
    <Document title={`${labels.title} · ${stamp.programName}`}>
      <PdfPage size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.networkName}>{brand.networkName}</Text>
          <Text style={styles.docTitle}>{labels.title}</Text>
          <View style={[styles.brandBar, { backgroundColor: brand.primaryColor }]} />
          <Text style={styles.programName}>
            {stamp.programName} · {stamp.capCode} · {stamp.level}
          </Text>
          <Text style={styles.seminaryName}>{stamp.seminaryName}</Text>
          <View style={styles.stampRow}>
            <Stamp label={labels.round} value={String(stamp.round)} />
            <Stamp label={labels.version} value={String(stamp.version)} />
            <Stamp label={labels.submittedOn} value={stamp.submittedOn} />
            {stamp.auditRunOn && <Stamp label={labels.auditRunOn} value={stamp.auditRunOn} />}
            <Stamp label={labels.generatedOn} value={stamp.generatedOn} />
          </View>
        </View>

        {/* La portada primero, igual que en pantalla. */}
        <View style={styles.cover}>
          <View style={styles.coverHead}>
            <Text style={styles.coverTitle}>{labels.coverTitle}</Text>
            <Text style={styles.statusChip}>{cover.statusLabel}</Text>
            {cover.stacked && <Text style={styles.statusChip}>{cover.stacked}</Text>}
          </View>
          <Text style={styles.coverMeta}>
            {cover.tcuLine} · {cover.evidenceLine}
          </Text>
          {cover.findings.length === 0 && cover.nothingToFlag ? (
            <Text style={styles.findingText}>{cover.nothingToFlag}</Text>
          ) : (
            cover.findings.map((f, i) => (
              <View key={i} style={styles.finding} wrap={false}>
                <Text style={[styles.findingTag, { color: SEVERITY_COLOR[f.severity] }]}>
                  {f.severityLabel}
                </Text>
                <Text style={styles.findingText}>{f.text}</Text>
              </View>
            ))
          )}
          <Text style={styles.reading}>{cover.reading}</Text>
        </View>

        <Text style={styles.sectionTitle}>{labels.auditTitle}</Text>
        <View style={styles.row}>
          <Text style={[styles.th, styles.cellName]}>{labels.area}</Text>
          <Text style={[styles.th, styles.cellNum]}>{labels.minTcu}</Text>
          <Text style={[styles.th, styles.cellNum]}>{labels.actualTcu}</Text>
          <Text style={[styles.th, styles.cellState]}>{labels.state}</Text>
        </View>
        {areas.map((a, i) => (
          <View key={i} style={styles.row} wrap={false}>
            <Text style={styles.cellName}>{a.name}</Text>
            <Text style={styles.cellNum}>{a.minTcu}</Text>
            <Text style={styles.cellNum}>{a.actualTcu}</Text>
            <Text style={[styles.cellState, ...(a.met ? [] : [styles.shortText])]}>
              {a.met ? labels.met : labels.short}
            </Text>
          </View>
        ))}

        {courses.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>{labels.coursesTitle}</Text>
            {courses.map((c, i) => (
              <View key={i} style={styles.row} wrap={false}>
                <Text style={styles.cellName}>
                  {c.code} · {c.name}
                </Text>
                <Text style={[styles.cellName, { flex: 2 }]}>{c.area}</Text>
                <Text style={styles.cellNum}>{c.tcu}</Text>
              </View>
            ))}
          </>
        )}

        <Text style={styles.sectionTitle}>{labels.sectionsTitle}</Text>
        {sections.map((s, i) => (
          <View key={i} style={styles.sectionBlock} wrap={false}>
            <Text style={styles.sectionHeading}>{s.heading}</Text>
            {s.body && <Text style={styles.sectionBody}>{s.body}</Text>}
          </View>
        ))}

        {attestations.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>{labels.attestationsTitle}</Text>
            {attestations.map((a, i) => (
              <Text key={i} style={styles.bullet}>
                • {a}
              </Text>
            ))}
          </>
        )}

        {data.observations && (
          <>
            <Text style={styles.sectionTitle}>{labels.observationsTitle}</Text>
            <Text style={styles.observations}>{data.observations}</Text>
          </>
        )}

        <Text style={styles.footer} fixed>
          {labels.footerDisclaimer}
        </Text>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </PdfPage>
    </Document>
  );
}
